/* Universal Dawn cart drawer — no product, brand, collection or hard-coded URL.
   Replace assets/cart-drawer.js in a DUPLICATED theme and test before publishing. */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this._renderQueue = Promise.resolve();
      this._openTimer = null;
      this._focusTimer = null;
      this._lastTrigger = null;
      this._overlayClick = () => this.close();
      this._documentKeydown = (event) => {
        if (event.key === 'Escape' && this.classList.contains('active')) {
          event.preventDefault();
          this.close();
        }
      };
      this._cartLinkClick = (event) => {
        event.preventDefault();
        this.open(event.currentTarget);
      };
      this._cartLinkKeydown = (event) => {
        if (event.key === ' ' || event.code === 'Space') {
          event.preventDefault();
          this.open(event.currentTarget);
        }
      };
      this.bindOverlay();
      this.setHeaderCartIconAccessibility();
    }

    connectedCallback() {
      this.bindOverlay();
      this.setHeaderCartIconAccessibility();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._documentKeydown);
      this.releasePageLock();
    }

    bindOverlay() {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (this._boundOverlay === overlay) return;
      if (this._boundOverlay) this._boundOverlay.removeEventListener('click', this._overlayClick);
      this._boundOverlay = overlay;
      overlay?.addEventListener('click', this._overlayClick);
    }

    setHeaderCartIconAccessibility() {
      const link = document.querySelector('#cart-icon-bubble');
      if (this._boundCartLink === link) return;
      if (this._boundCartLink) {
        this._boundCartLink.removeEventListener('click', this._cartLinkClick);
        this._boundCartLink.removeEventListener('keydown', this._cartLinkKeydown);
      }
      this._boundCartLink = link;
      if (!link) return;
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
      link.addEventListener('click', this._cartLinkClick);
      link.addEventListener('keydown', this._cartLinkKeydown);
    }

    hasValidMarkup() {
      return Boolean(this.querySelector('#CartDrawer .drawer__inner') && this.querySelector('#CartDrawer-Overlay') && this.querySelector('.drawer__close'));
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      if (!this.hasValidMarkup()) {
        console.error('[Universal Dawn cart] Drawer markup missing; not opening.');
        this.close();
        return;
      }
      this.bindOverlay();
      const note = this.querySelector('[id^="Details-"] summary');
      if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);
      if (this.classList.contains('active')) return;

      clearTimeout(this._openTimer);
      clearTimeout(this._focusTimer);
      this._openTimer = setTimeout(() => {
        if (!this.isConnected || !this.hasValidMarkup()) return;
        this.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this._documentKeydown);
        try { this.querySelector('cart-drawer-items')?.dispatchViewEvent?.(); }
        catch (error) { console.warn('[Universal Dawn cart] View event:', error); }
        this._focusTimer = setTimeout(() => {
          if (!this.classList.contains('active')) return;
          const focusContainer = this.classList.contains('is-empty')
            ? (this.querySelector('.drawer__inner-empty') || this.querySelector('#CartDrawer'))
            : this.querySelector('#CartDrawer');
          const focusTarget = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
          try {
            if (typeof trapFocus === 'function') trapFocus(focusContainer, focusTarget);
            else focusTarget?.focus?.();
          } catch (error) {
            console.warn('[Universal Dawn cart] Focus fallback:', error);
            focusTarget?.focus?.();
          }
        }, 320);
      }, 0);
    }

    close() {
      clearTimeout(this._openTimer);
      clearTimeout(this._focusTimer);
      this.classList.remove('active', 'animate');
      this.releasePageLock();
      try {
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
        else this.activeElement?.focus?.();
      } catch (error) {
        console.warn('[Universal Dawn cart] Focus release:', error);
        this.activeElement?.focus?.();
      }
    }

    releasePageLock() {
      document.removeEventListener('keydown', this._documentKeydown);
      document.body.classList.remove('overflow-hidden');
    }

    setSummaryAccessibility(summary) {
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', 'false');
      const next = summary.nextElementSibling;
      if (next?.id) summary.setAttribute('aria-controls', next.id);
      summary.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', String(!event.currentTarget.closest('details')?.hasAttribute('open')));
      });
      if (typeof onKeyUpEscape === 'function') summary.parentElement?.addEventListener('keyup', onKeyUpEscape);
    }

    // Queue render requests rather than silently dropping rapid updates.
    renderContents(state) {
      const task = this._renderQueue.catch(() => {}).then(() => this._renderContents(state));
      this._renderQueue = task;
      return task;
    }

    async _renderContents(state) {
      try {
        let sections = state?.sections || {};
        let nextDrawer = this.getSectionDOM(sections['cart-drawer'], '#CartDrawer');
        let itemCount = null;
        const valid = (node, count) => {
          if (!node?.querySelector('.drawer__inner') || !node.querySelector('#CartDrawer-Overlay')) return false;
          if (count > 0 && !node.querySelector('.cart-item, [id^="CartDrawer-Item-"], .cart-items tbody tr')) return false;
          return true;
        };

        // Verify cart state only when necessary, then fetch fresh sections if incomplete.
        if (!valid(nextDrawer, null)) {
          sections = { ...sections, ...(await this.fetchSections()) };
          nextDrawer = this.getSectionDOM(sections['cart-drawer'], '#CartDrawer');
        }
        if (!valid(nextDrawer, null)) throw new Error('Shopify returned invalid drawer markup');
        if (!nextDrawer.querySelector('.cart-item, [id^="CartDrawer-Item-"], .cart-items tbody tr')) {
          const cart = await this.fetchCart();
          itemCount = cart.item_count;
          if (itemCount > 0) {
            sections = { ...sections, ...(await this.fetchSections()) };
            nextDrawer = this.getSectionDOM(sections['cart-drawer'], '#CartDrawer');
          }
        }
        if (!valid(nextDrawer, itemCount)) throw new Error('Cart drawer is missing its product lines');
        const current = this.querySelector('#CartDrawer');
        if (!current) throw new Error('Current drawer container is missing');

        // Replace only inner HTML; preserve the custom element and its listeners.
        current.innerHTML = nextDrawer.innerHTML;
        this.classList.toggle('is-empty', Boolean(nextDrawer.querySelector('.drawer__inner-empty')));
        this.productId = state?.id;
        this.bindOverlay();
        const bubble = document.getElementById('cart-icon-bubble');
        const bubbleHTML = this.getSectionDOM(sections['cart-icon-bubble'], '#cart-icon-bubble');
        if (bubble && bubbleHTML) bubble.innerHTML = bubbleHTML.innerHTML;
        this.setHeaderCartIconAccessibility();
        this.open();
        return true;
      } catch (error) {
        console.error('[Universal Dawn cart] Unable to update drawer:', error);
        this.close();
        this.dispatchEvent(new CustomEvent('universal-cart:error', { detail: { message: error.message }, bubbles: true }));
        return false;
      }
    }

    async fetchSections() {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(root, window.location.origin);
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Section refresh failed (${response.status})`);
      return response.json();
    }

    async fetchCart() {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
      return response.json();
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return this.getSectionDOM(html, selector)?.innerHTML || '';
    }

    getSectionDOM(html, selector = '.shopify-section') {
      if (typeof html !== 'string' || !html.trim()) return null;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector(selector);
    }

    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
    }

    setActiveElement(element) { this.activeElement = element; }
  }
  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
      ];
    }
  }
  customElements.define('cart-drawer-items', CartDrawerItems);
}
